import { useMemo } from "react";
import {
  DataExplorationSeries,
  isSelectableSeries
} from "reducers/configurableChartReducer";
import {
  LineSeries,
  ReferenceLine,
  ScadaDatetimeTagDataSource,
  YAxis
} from "interfaces/chart";
import { Tag } from "interfaces/tag";
import { Machine } from "interfaces/machine";
import { useGetMinMaxTagsMachines } from "hooks/data/useGetMinMaxTagsMachines";

/**
 * Gets the list of selectable series from the series provided
 * Selectable series are those which can be selected by the user in the series section of the chart menu
 * @param series a list of unfiltered chart series
 */
export const useSelectableSeries = (
  series: DataExplorationSeries[]
): LineSeries<ScadaDatetimeTagDataSource>[] => {
  return useMemo(() => series.filter(isSelectableSeries), [series]);
};

/**
 * Gets the selected series by id from a list of selectable series
 * @param selectedSeriesId the id of the currently selected series
 * @param selectableSeries a list containing only currently selectable series
 */
export const useSelectedSeries = (
  selectedSeriesId: string | null,
  selectableSeries: LineSeries<ScadaDatetimeTagDataSource>[]
): LineSeries<ScadaDatetimeTagDataSource> | null => {
  return useMemo(() => {
    if (selectedSeriesId === null) {
      return null;
    }

    return selectableSeries.find(x => x.id === selectedSeriesId) ?? null;
  }, [selectableSeries, selectedSeriesId]);
};

/**
 * Gets the currently selected y-axis from a list
 * @param selectedYAxisId the id of the currently selected y-axis
 * @param yAxes the list of all y-axes
 */
export const useSelectedYAxis = (
  selectedYAxisId: string | null,
  yAxes: YAxis[]
): YAxis | null => {
  return useMemo(() => {
    if (selectedYAxisId === null) {
      return null;
    }

    return yAxes.find(axis => axis.id === selectedYAxisId) ?? null;
  }, [yAxes, selectedYAxisId]);
};

/**
 * Gets the currently selected machine based on specific series
 * @param selectedSeries the currently selected series
 * @param machines the list of all machines
 */
export const useSelectedMachine = (
  selectedSeries: LineSeries<ScadaDatetimeTagDataSource> | null,
  machines: Machine[]
): Machine | null => {
  return useMemo(() => {
    if (selectedSeries === null) {
      return null;
    }

    return (
      machines.find(x => x.id === selectedSeries.dataSource.args.machineId) ??
      null
    );
  }, [selectedSeries, machines]);
};

/**
 * gets the currently selected parameter based on a specific series
 * @param selectedSeries the currently selected series
 * @param tags the list of all tags
 */
export const useSelectedParameter = (
  selectedSeries: LineSeries<ScadaDatetimeTagDataSource> | null,
  tags: Tag[]
): Tag | null => {
  return useMemo(() => {
    if (selectedSeries === null) {
      return null;
    }

    return (
      tags.find(x => x.id === selectedSeries.dataSource.args.tagId) ?? null
    );
  }, [selectedSeries, tags]);
};

/**
 * Gets the range over a list of scada datetime series
 * @param series
 */
export const useGetSeriesRange = (
  series: LineSeries<ScadaDatetimeTagDataSource>[]
): {
  loading: boolean;
  error: boolean;
  range: { min_date: string; max_date: string } | undefined;
} => {
  // Destructure arrays of selected machineIds and tagIds when both machine and tag are selected for a series.
  const { machineIds, tagIds } = useMemo(() => {
    let machineIds: number[] = [];
    let tagIds: number[] = [];

    series.forEach(series => {
      if (series.dataSource.args.machineId && series.dataSource.args.tagId) {
        machineIds.push(series.dataSource.args.machineId);
        tagIds.push(series.dataSource.args.tagId);
      }
    });

    return { machineIds, tagIds };
  }, [series]);

  return useGetMinMaxTagsMachines(machineIds, tagIds);
};

/**
 * Gets the selected reference line by id from a list of plot lines
 * @param selectedReferenceLineId the id of the currently selected line
 * @param referenceLines a list of reference lines from the chart
 */
export const useSelectedReferenceLine = (
  selectedReferenceLineId: string | null,
  referenceLines: ReferenceLine[]
): ReferenceLine | null => {
  return useMemo(() => {
    if (selectedReferenceLineId === null) {
      return null;
    }

    return referenceLines?.find(x => x.id === selectedReferenceLineId) ?? null;
  }, [referenceLines, selectedReferenceLineId]);
};
